import { logError, logWarning } from '@/utils/miscUtils';
import { SubmitFormPayload } from '@/components/ConvertFlowQuiz/ConvertFlowQuiz.helpers';

// Bound the request so a hung backend can't trap the caller. The CF submit
// gate disables its button while this is in flight and only re-enables it when
// this settles, so without a timeout a stalled POST leaves the button stuck.
const DEFAULT_TIMEOUT_MS = 15_000;

export default async function postFormSubmit(
	url: string,
	payload: SubmitFormPayload,
	headers: { [key: string]: string } = {},
	timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<unknown> {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);
	try {
		const response = await fetch(url, {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'Content-Type': 'application/json',
				...headers,
			},
			signal: controller.signal,
		});

		const data = (await response.json()) as unknown;

		if (response.status === 400) {
			logWarning('postFormSubmit: 400 validation error');
			return data;
		}

		if (response.status === 403) {
			logWarning('postFormSubmit: 403 fidelity check failed');
			logError('403: Lead failed fidelity check, returning `data` for local handling.');
			return data;
		}

		if (response.ok) {
			return data;
		}

		throw new Error(`Unexpected response status: ${response.status}`);
	} catch (err: unknown) {
		const aborted = err instanceof Error && err.name === 'AbortError';
		const message = aborted ? `timed out after ${timeoutMs}ms` : err instanceof Error ? err.message : 'unknown';
		logError(`postFormSubmit error: ${message}`);
		throw err;
	} finally {
		clearTimeout(timer);
	}
}
